"use client";
import { useEffect, useRef, useState, ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { CustomTooltip } from "@/app/components/ui";
import { cn } from "@/app/lib/utils";

const COLLAPSED_LINE_CLAMP_CLASS = "line-clamp-4";

/**
 * Opaque match for user bubble `bg-accent-foreground/5`.
 * Uses theme CSS vars so light/dark both resolve correctly.
 */
const BUBBLE_SURFACE =
   "color-mix(in srgb, hsl(var(--accent-foreground)) 5%, hsl(var(--background)))";

/** Transparent end in the same hue family as `--background` (light & dark safe). */
const BUBBLE_SURFACE_CLEAR = "hsl(var(--background) / 0)";

type CollapsibleMessageContentProps = {
   children: ReactNode;
   className?: string;
};

export const CollapsibleMessageContent = ({
   children,
   className
}: CollapsibleMessageContentProps) => {
   const translate = useTranslations("Chat.CollapsibleMessage");
   const contentRef = useRef<HTMLDivElement>(null);
   const [isExpanded, setIsExpanded] = useState(false);
   const [isOverflowing, setIsOverflowing] = useState(false);

   useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
   }, [children]);

   const toggleButton = isOverflowing && (
      <CustomTooltip
         text={isExpanded ? translate("showLess") : translate("showMore")}
         position="bottom"
         align="end"
      >
         <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            className={cn(
               // Pill matches copy-button size; bg-background works in both themes
               "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background text-muted-foreground ring-1 ring-border transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary hover:ring-primary/40",
               isExpanded && "shadow-sm"
            )}
         >
            <ChevronDown
               className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")}
            />
            <span className="sr-only">
               {isExpanded ? translate("showLess") : translate("showMore")}
            </span>
         </button>
      </CustomTooltip>
   );

   const isCollapsedWithOverflow = !isExpanded && isOverflowing;

   return (
      <div className={cn(isCollapsedWithOverflow && "relative")}>
         <div
            ref={contentRef}
            className={cn(className, !isExpanded && COLLAPSED_LINE_CLAMP_CLASS)}
         >
            {children}
         </div>
         {isExpanded && isOverflowing && (
            <div className="mt-1 flex justify-end">{toggleButton}</div>
         )}
         {isCollapsedWithOverflow && (
            <>
               {/*
                 Gemini-style fade: capsule (rounded-full) + light blur
                 so rectangular corners never show through the text.
               */}
               <div
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 right-0 z-[1] h-6 w-32 rounded-full blur-[3px]"
                  style={{
                     // `in oklab` + CLEAR (not keyword transparent) keeps light & dark edges clean
                     background: `linear-gradient(to left in oklab, ${BUBBLE_SURFACE} 0%, ${BUBBLE_SURFACE} 25%, ${BUBBLE_SURFACE_CLEAR} 100%)`
                  }}
               />
               <div className="absolute bottom-0 right-0 z-10">{toggleButton}</div>
            </>
         )}
      </div>
   );
};
